// Shared derivation for a candidate's selection-step sub-journey. Used by the
// Deliver ▸ Interviews surface (SelectionJourneyStage) and the pipeline/funnel
// overview (table, snapshot, funnel header) so all surfaces label steps and
// pick the "current" step the same way.

export interface SelectionStep {
  id: string;
  continuityId?: string | null;
  round: number | null;
  status: string;
  outcome: string;
  rating: number | null;
  stepType: string | null;
  label: string | null;
}

// A step as defined in the campaign's SelectionProcess (the ordered skeleton
// that candidate steps are materialised from), keyed by continuity.
export interface ProcessStep {
  id: string;
  continuityId: string;
  stepType: string | null;
  label: string | null;
  position: number;
}

export const STEP_TYPE_LABELS: Record<string, string> = {
  recruiter_call: "Recruiter call",
  recruiter_interview: "Recruiter interview",
  interview: "Interview",
  psychometric: "Psychometric",
  final: "Final",
};

// Step types whose evidence is interview-shaped — they get an assessment plan
// (intent + coverage rows). Other types (psychometric and any unknown future
// type) use evidence pathways that don't fit the plan model.
export const INTERVIEW_FLAVOURED_STEP_TYPES = new Set<string>([
  "recruiter_call",
  "recruiter_interview",
  "interview",
  "final",
]);

export function isInterviewFlavoured(stepType: string | null | undefined): boolean {
  return stepType != null && INTERVIEW_FLAVOURED_STEP_TYPES.has(stepType);
}

// A single coverage row attached to a Step Assessment Plan — picks one
// CapabilityDimension (continuityId) and a depth that step will probe it at.
// `dimensionName` and `dimensionInCurrentMatrix` are server-resolved labels
// so the UI can render meaningful text even for historical entries whose
// dimension is no longer in the current Assessment Matrix.
export interface StepPlanCoverageEntry {
  dimensionId: string;
  depth: string;
  dimensionName?: string | null;
  dimensionInCurrentMatrix?: boolean;
}

// The plan attached to a process step: what the step is trying to learn
// (intent) plus the dimensions it will cover. Carried alongside structural
// fields on SelectionProcessStep so the cohesive editor can diff and save
// structure + plan in one pass.
export interface StepAssessmentPlanData {
  intent: string | null;
  coverage: StepPlanCoverageEntry[];
  versionNumber: number | null;
  versionOrigin: string | null;
  acknowledgedAt: string | null;
}

// A single step row in the SelectionProcessEditor: the structural fields the
// brief mutates (step type, label, format, duration, participants, purpose)
// plus the assessment plan that hangs off it.
//
// Distinct from `SelectionStep` (above), which describes a candidate's
// materialised step within their journey. Both names are stable — the
// editor-facing record lives here so the diff utility and editor can share it.
export interface SelectionProcessStep {
  id?: string;
  continuityId?: string;
  stepType: string;
  label: string | null;
  stepFormat?: string | null;
  durationMinutes?: number | null;
  participants?: StepParticipant[];
  purpose?: string | null;
  inFlightCandidateCount?: number;
  assessmentPlan?: StepAssessmentPlanData;
}

export interface StepParticipant {
  role: string;
  name: string | null;
  organisationMembershipId: string | null;
}

// A step continuity whose latest version has been deactivated — surfaced by
// `SelectionProcess.archivedSteps` so the cohesive editor can show "Removed"
// rows and offer a one-click Restore. Sorted server-side by removedAt DESC.
export interface ArchivedStep {
  continuityId: string;
  lastLabel: string | null;
  lastStepType: string | null;
  removedAt: string | null;
  removedBy: string | null;
  changeReason: string | null;
  inFlightCandidateCount: number;
}

// Display name for a step: its explicit label, else a humanised step type,
// else a round-numbered fallback. Accepts either a materialised candidate step
// or a defined process step (both carry label + stepType).
export function stepDisplayLabel(step: {
  label: string | null;
  stepType: string | null;
  round?: number | null;
}): string {
  if (step.label) return step.label;
  if (step.stepType) return STEP_TYPE_LABELS[step.stepType] ?? step.stepType;
  return `Step ${step.round ?? "?"}`;
}

// The candidate's current step: the first not-yet-completed step in order.
// Null when the candidate has no steps or every step is complete.
export function currentSelectionStep(steps: SelectionStep[]): SelectionStep | null {
  return steps.find((s) => s.status !== "completed") ?? null;
}

export interface InterviewStepBucket {
  key: string;
  label: string;
  count: number;
}

interface BucketCandidate {
  stage: string;
  candidateSelectionSteps: SelectionStep[];
}

// Distribution of interview-stage candidates across the defined selection
// process. The breakdown mirrors the process skeleton: one bucket per defined
// step in order (shown even at zero, like the macro funnel), counting
// candidates whose *current* step is that step. Candidates sitting on a step
// no longer in the process get their own trailing bucket. An "awaiting" bucket
// counts interview candidates with no steps yet; a "complete" bucket counts
// those past their last step. Both appended only when non-zero.
//
// When no defined process is available, falls back to bucketing only the
// distinct current steps that candidates actually sit on.
export function interviewStepBuckets(
  candidates: BucketCandidate[],
  processSteps: ProcessStep[] = [],
): InterviewStepBucket[] {
  const ordered = [...processSteps].sort((a, b) => a.position - b.position);
  const definedKeys = new Map<string, InterviewStepBucket>();
  for (const step of ordered) {
    definedKeys.set(step.continuityId, {
      key: `step:${step.continuityId}`,
      label: stepDisplayLabel(step),
      count: 0,
    });
  }

  // Orphan steps: a candidate's current step whose continuity isn't (or is no
  // longer) in the defined process, keyed so they aggregate together.
  const orphans = new Map<string, { label: string; round: number; count: number }>();
  let awaiting = 0;
  let complete = 0;

  for (const cc of candidates) {
    if (cc.stage !== "interview") continue;
    const steps = cc.candidateSelectionSteps ?? [];
    if (steps.length === 0) {
      awaiting += 1;
      continue;
    }
    const current = currentSelectionStep(steps);
    if (!current) {
      complete += 1;
      continue;
    }
    const defined = current.continuityId ? definedKeys.get(current.continuityId) : undefined;
    if (defined) {
      defined.count += 1;
      continue;
    }
    const key = current.continuityId ?? current.id;
    const existing = orphans.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      orphans.set(key, {
        label: stepDisplayLabel(current),
        round: current.round ?? Number.MAX_SAFE_INTEGER,
        count: 1,
      });
    }
  }

  // No defined process: fall back to showing only steps candidates sit on.
  const buckets: InterviewStepBucket[] = ordered.length > 0 ? [...definedKeys.values()] : [];

  const orphanBuckets = [...orphans.entries()]
    .sort((a, b) => a[1].round - b[1].round)
    .map(([key, v]) => ({ key: `step:${key}`, label: v.label, count: v.count }));
  buckets.push(...orphanBuckets);

  if (complete > 0) buckets.push({ key: "complete", label: "All steps complete", count: complete });
  if (awaiting > 0) buckets.push({ key: "awaiting", label: "Awaiting setup", count: awaiting });

  return buckets;
}
